/**
 * Bienvenida, extensión primero (SDD §7.7): la extensión la abre al
 * instalarse. Pasos: cuenta → extensión instalada → conectada → aviso a los
 * participantes → prueba de 30 segundos para ver todo el flujo funcionando.
 */
import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { useApp, useToast, errorText } from '../components/AppContext'
import { Brand } from '../components/ui'
import { auth } from '../lib/auth'
import {
  browserName,
  connectExtension,
  detectExtension,
  startTestRecording,
  type ExtensionInfo
} from '../lib/extension'
import { useDocumentTitle, usePolling } from '../lib/hooks'

// Edge instala desde la tienda de Chrome; cuando esté en Edge Add-ons, poner aquí esa ficha
const FICHA_CHROME =
  'https://chromewebstore.google.com/detail/eco-notas-de-reuniones/najghlnebjahfdjfjmbohnlbkgnalpoo'
const STORE_URL = {
  chrome: FICHA_CHROME,
  edge: FICHA_CHROME,
  otro: FICHA_CHROME
}

export function Welcome() {
  useDocumentTitle('Bienvenida')
  const { session, ready, api, me, refreshMe } = useApp()
  const toast = useToast()
  const nav = useNavigate()
  const [ext, setExt] = useState<ExtensionInfo | null | undefined>(undefined)
  const [connecting, setConnecting] = useState(false)
  const [testing, setTesting] = useState<'idle' | 'recording' | 'processing' | 'stuck'>('idle')
  const [testCount, setTestCount] = useState(30)
  const [baseline, setBaseline] = useState<string | null>(null)
  // Entre el clic y la respuesta de la extensión: un doble clic no inicia dos pruebas
  const [starting, setStarting] = useState(false)

  const probe = useCallback(async () => setExt(await detectExtension()), [])
  // Analítica: la extensión llegó a la bienvenida con sesión (una vez por navegador)
  useEffect(() => {
    if (!session || !ext) return
    try {
      if (localStorage.getItem('eco.ev.extension_instalada')) return
      localStorage.setItem('eco.ev.extension_instalada', '1')
    } catch {
      return
    }
    void api.event('extension_instalada', { navegador: browserName() })
  }, [session, ext, api])
  useEffect(() => {
    void probe()
  }, [probe])
  // Mientras falte instalar, reintentar (el usuario vuelve de la tienda a esta pestaña)
  usePolling(() => void probe(), 3000, ext === null || ext?.pinned === false)

  const connected = Boolean(ext?.connected)
  const consent = Boolean(me?.user.consentimientoAceptadoEn)

  // Conectar solo automáticamente la primera vez: un clic menos para el usuario
  useEffect(() => {
    if (session && ext && !ext.connected && !connecting) void connect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, ext?.id, ext?.connected])

  async function connect() {
    if (!ext) return
    setConnecting(true)
    try {
      const { hashedToken } = await api.extensionLink()
      if (await connectExtension(ext.id, hashedToken)) await probe()
      else toast('La extensión no respondió. Recarga esta página.', 'error')
    } catch (e) {
      toast(errorText(e), 'error')
    } finally {
      setConnecting(false)
    }
  }

  async function test() {
    if (!ext || starting) return
    setStarting(true)
    try {
      const list = await api.listRecordings({ limit: 1 })
      setBaseline(list.items[0]?.id ?? '')
      const ok = await startTestRecording(ext.id, 30)
      if (!ok)
        return toast(
          'No se pudo iniciar la prueba. Revisa el permiso del micrófono en la extensión.',
          'error'
        )
      setTesting('recording')
      setTestCount(30)
    } catch (e) {
      toast(errorText(e), 'error')
    } finally {
      setStarting(false)
    }
  }
  useEffect(() => {
    if (testing !== 'recording') return
    const id = setInterval(() => setTestCount((c) => (c > 0 ? c - 1 : 0)), 1000)
    const done = setTimeout(() => setTesting('processing'), 32_000)
    return () => {
      clearInterval(id)
      clearTimeout(done)
    }
  }, [testing])
  // Si en 2 minutos no aparece la grabación, algo falló en la extensión: no esperar para siempre
  useEffect(() => {
    if (testing !== 'processing') return
    const t = setTimeout(() => setTesting('stuck'), 120_000)
    return () => clearTimeout(t)
  }, [testing])
  usePolling(
    () =>
      void api.listRecordings({ limit: 1 }).then((l) => {
        const r = l.items[0]
        if (r && r.id !== baseline) nav(`/r/${r.id}`)
      }),
    3000,
    testing === 'processing'
  )

  if (!ready) return null
  const browser = browserName()

  return (
    <div className="public">
      <div className="public-head">
        <Brand to="/" />
        {session && (
          <Link to="/" className="faint">
            Ir a mi biblioteca →
          </Link>
        )}
      </div>
      <div className="page narrow">
        <h1 style={{ marginBottom: 6 }}>Te damos la bienvenida a eco</h1>
        <p className="muted" style={{ marginBottom: 20 }}>
          Cuatro pasos y todo queda listo para tu próxima reunión.
        </p>
        <ol className="steps">
          <li className={session ? 'done' : ''}>
            <div className="grow stack" style={{ gap: 6 }}>
              <strong>Crea tu cuenta</strong>
              {session ? (
                <span className="muted">Entraste como {session.email}</span>
              ) : (
                <div className="row wrap">
                  <button
                    className="btn primary"
                    onClick={() => void auth.signInWithGoogle('/bienvenida?ext=1')}
                  >
                    Continuar con Google
                  </button>
                  <Link className="btn" to="/login?next=%2Fbienvenida%3Fext%3D1">
                    Usar mi correo
                  </Link>
                </div>
              )}
            </div>
          </li>
          <li className={ext ? 'done' : ''}>
            <div className="grow stack" style={{ gap: 6 }}>
              <strong>Instala la extensión</strong>
              {ext ? (
                <span className="muted">
                  Instalada (versión {ext.version}).{' '}
                  {ext.pinned === true
                    ? 'Ya está fijada en la barra. ✓'
                    : 'Fíjala en la barra: haz clic en el ícono de pieza de rompecabezas (arriba a la derecha) y luego en el alfiler junto a eco.'}
                </span>
              ) : ext === null ? (
                <div className="row wrap">
                  <a
                    className="btn"
                    href={STORE_URL[browser]}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Añadir a {browser === 'edge' ? 'Edge' : 'Chrome'}
                  </a>
                  <span className="faint" style={{ fontSize: 12 }}>
                    Cuando termine, vuelve a esta pestaña.
                  </span>
                </div>
              ) : (
                <span className="faint">Buscando…</span>
              )}
            </div>
          </li>
          <li className={connected ? 'done' : ''}>
            <div className="grow stack" style={{ gap: 6 }}>
              <strong>Conecta la extensión con tu cuenta</strong>
              {connected ? (
                <span className="muted">Conectada.</span>
              ) : (
                <div>
                  <button
                    className="btn"
                    disabled={!session || !ext || connecting}
                    onClick={() => void connect()}
                  >
                    {connecting ? 'Conectando…' : 'Conectar'}
                  </button>
                </div>
              )}
            </div>
          </li>
          <li className={consent ? 'done' : ''}>
            <div className="grow stack" style={{ gap: 6 }}>
              <strong>Avisa antes de grabar</strong>
              <span className="muted" style={{ fontSize: 13 }}>
                eco no entra como bot a la llamada: nadie ve que grabas. Por eso te pedimos avisar a
                los participantes cada vez. En Chile, grabar una conversación privada sin
                consentimiento puede ser delito.
              </span>
              {!consent && (
                <div>
                  <button
                    className="btn"
                    disabled={!session}
                    onClick={() =>
                      void api
                        .patchMe({ aceptarConsentimiento: true })
                        .then(() => refreshMe())
                        .catch((e) => toast(errorText(e), 'error'))
                    }
                  >
                    Me comprometo a avisar
                  </button>
                </div>
              )}
            </div>
          </li>
          <li>
            <div className="grow stack" style={{ gap: 6 }}>
              <strong>Haz una prueba de 30 segundos</strong>
              <span className="muted" style={{ fontSize: 13 }}>
                Graba solo tu micrófono: cuenta qué harás esta semana. En un minuto verás la
                transcripción y las notas. Consume 1 minuto de tus horas.
              </span>
              {testing === 'recording' ? (
                <span className="pill live" aria-live="polite">
                  <span className="dot" /> Grabando… {testCount} s
                </span>
              ) : testing === 'processing' ? (
                <span className="pill work" aria-live="polite">
                  <span className="dot" /> Procesando tu prueba…
                </span>
              ) : testing === 'stuck' ? (
                <div className="notice error stack" role="alert" style={{ gap: 6 }}>
                  <span>
                    La prueba no llegó. Revisa el permiso del micrófono y la conexión, y vuelve a
                    intentarlo.
                  </span>
                  <div className="row wrap">
                    <button className="btn sm" onClick={() => setTesting('idle')}>
                      Volver a intentar
                    </button>
                    <Link className="btn sm ghost" to="/ayuda#problemas">
                      Ver ayuda
                    </Link>
                  </div>
                </div>
              ) : (
                <div className="row wrap">
                  <button
                    className="btn live"
                    disabled={!connected || !consent || starting}
                    onClick={() => void test()}
                  >
                    ● Grabar prueba
                  </button>
                  <Link className="btn ghost" to="/import">
                    o sube un audio
                  </Link>
                </div>
              )}
            </div>
          </li>
        </ol>
        <section className="card stack" style={{ gap: 6, marginTop: 20 }}>
          <h2 style={{ fontSize: 15, margin: 0 }}>Tres formas de grabar</h2>
          <ul className="muted" style={{ margin: 0, paddingLeft: 18, fontSize: 13.5 }}>
            <li>
              <strong>En el navegador</strong> (Meet, Teams o Zoom web): abre la reunión y pulsa el
              ícono de eco o <kbd>Alt+Shift+R</kbd>.
            </li>
            <li>
              <strong>En una app de escritorio</strong> (Zoom o Teams instalados, en Windows): en el
              ícono de eco elige «App de escritorio» y marca «Compartir audio del sistema».
            </li>
            <li>
              <strong>Presencial</strong>: en una sala, solo con el micrófono.
            </li>
          </ul>
        </section>
      </div>
    </div>
  )
}
